import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { activateOnEnterOrSpace } from '../utils/a11y';

export default function Settings() {
  const { theme, toggleTheme } = useApp();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Account');
  const [person, setPerson] = useState(null);
  const [notifications, setNotifications] = useState({
    jobMatches: true, rewards: true,
    assignments: true, batches: true, payments: true,
  });

  useEffect(() => {
    if (!user?.id) return;
    supabase.from('individual')
      .select('*')
      .eq('id', user.id).single()
      .then(({ data }) => {
        if (data) {
          setPerson(data);
          setNotifications({
            jobMatches: data.notif_job_matches ?? true, rewards: data.notif_rewards ?? true,
            assignments: data.notif_assignments ?? true, batches: data.notif_batches ?? true, payments: data.notif_payments ?? true,
          });
        }
      });
  }, [user?.id]);

  const PREF_COLUMN_MAP = { jobMatches: 'notif_job_matches', rewards: 'notif_rewards', assignments: 'notif_assignments', batches: 'notif_batches', payments: 'notif_payments' };

  const toggleNotification = async (key) => {
    const newValue = !notifications[key];
    setNotifications(prev => ({ ...prev, [key]: newValue }));
    const column = PREF_COLUMN_MAP[key];
    if (column && user?.id) {
      const { error } = await supabase.from('individual').update({ [column]: newValue }).eq('id', user.id);
      if (error) {
        setNotifications(prev => ({ ...prev, [key]: !newValue }));
        window.dispatchEvent(new CustomEvent('juuni:error', { detail: { message: 'Failed to save preference.' } }));
      }
    }
  };

  return (
    <div>
      {/* Header — matches boss Settings exactly */}
      <div style={{ marginBottom: 4 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', margin: 0 }}>Settings</h1>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: '4px 0 0' }}>Notifications, display, account, and privacy</p>
      </div>

      {/* Tab bar — identical to boss Settings */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {['Account', 'Work Preferences', 'Notifications', 'Display', 'Privacy'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '8px 16px', fontSize: 12, fontWeight: activeTab === tab ? 600 : 400,
            background: 'none', border: 'none', cursor: 'pointer',
            color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
            borderBottom: activeTab === tab ? '2px solid var(--text-primary)' : '2px solid transparent',
          }}>{tab}</button>
        ))}
      </div>

      {/* Work Preferences Tab — read-only display of current values */}
      {activeTab === 'Work Preferences' && (
        <div style={{ maxWidth: 600 }}>
          {[
            { icon: '⏰', label: 'Available Hours', value: person?.available_hours_per_week ? `${person.available_hours_per_week}h/week` : 'Not set' },
            { icon: '📅', label: 'Preferred Schedule', value: person?.preferred_schedule || 'Flexible' },
            { icon: '🎯', label: 'Primary Domain', value: person?.primary_domain || 'Not set' },
            { icon: '🌍', label: 'Timezone', value: person?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone },
            { icon: '💳', label: 'Payment Method', value: person?.payment_method_type ? `${person.payment_method_type}${person.payment_method_last4 ? ` ••••${person.payment_method_last4}` : ''}` : 'Not set' },
            { icon: '🔄', label: 'Open to Cross-Domain', value: person?.open_to_cross_domain ? 'Yes' : 'No' },
          ].map(setting => (
            <div key={setting.label} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 20px', background: "var(--bg-card)", borderRadius: 10,
              marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{setting.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{setting.label}</div>
              </div>
              <span style={{ fontSize: 13, color: setting.value === 'Not set' ? 'var(--border)' : 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{setting.value}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 12, textAlign: 'center' }}>
            Contact your project manager to update work preferences
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'Notifications' && (
        <div style={{ maxWidth: 600 }}>
          {[
            { key: 'jobMatches', icon: '🔍', label: 'Job Matches', desc: 'Get notified when jobs matching your profile are posted' },
            { key: 'rewards', icon: '🎁', label: 'Rewards & Boosts', desc: 'When boosts are earned, activated, or about to expire' },
            { key: 'assignments', icon: '📋', label: 'Assignments', desc: 'New assignments, onboarding complete, application updates' },
            { key: 'batches', icon: '📦', label: 'Batch Updates', desc: 'New batches assigned, revisions requested, batches accepted' },
            { key: 'payments', icon: '💰', label: 'Payments', desc: 'Payment processed confirmations' },
          ].map(setting => (
            <div key={setting.key} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 20px', background: "var(--bg-card)", borderRadius: 10,
              marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{setting.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{setting.label}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{setting.desc}</div>
              </div>
              <div
                role="switch"
                aria-checked={!!notifications[setting.key]}
                aria-label={`Toggle ${setting.label}`}
                tabIndex={0}
                onClick={() => toggleNotification(setting.key)}
                onKeyDown={activateOnEnterOrSpace(() => toggleNotification(setting.key))}
                style={{
                width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
                background: notifications[setting.key] ? 'var(--text-success)' : 'var(--border)',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: "var(--bg-card)",
                  position: 'absolute', top: 2,
                  left: notifications[setting.key] ? 20 : 2, transition: 'left 0.2s',
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Account Tab */}
      {activeTab === 'Account' && (
        <div style={{ maxWidth: 600 }}>
          {[
            { icon: '✉', label: 'Email', value: user?.email || '—', action: 'Change' },
            { icon: '🔒', label: 'Password', value: '••••••••••', action: 'Update' },
            { icon: '🛡', label: 'Two-Factor Authentication', value: 'Not configured', action: 'Set up' },
          ].map(item => (
            <div key={item.label} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 20px', background: "var(--bg-card)", borderRadius: 10,
              marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{item.label}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{item.value}</div>
              </div>
              <button onClick={() => alert('Contact support to update account settings')} style={{
                fontSize: 11, fontWeight: 600, padding: '6px 16px',
                background: "var(--bg)", color: "var(--text-primary)", border: 'none',
                borderRadius: 6, cursor: 'pointer',
              }}>{item.action}</button>
            </div>
          ))}
        </div>
      )}

      {/* Display Tab */}
      {activeTab === 'Display' && (
        <div style={{ maxWidth: 600 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '16px 20px', background: "var(--bg-card)", borderRadius: 10,
            marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
          }}>
            <span style={{ fontSize: 18 }}>🎨</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>Theme</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Choose your display theme</div>
            </div>
            <select value={theme} onChange={e => toggleTheme(e.target.value)} style={{
              fontSize: 12, padding: '6px 12px', border: '1px solid var(--border)',
              borderRadius: 6, background: "var(--bg-card)", color: "var(--text-primary)", outline: 'none', cursor: 'pointer',
            }}>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '16px 20px', background: "var(--bg-card)", borderRadius: 10,
            marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
          }}>
            <span style={{ fontSize: 18 }}>🌐</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>Language</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Interface language</div>
            </div>
            <span style={{ fontSize: 13, color: "var(--text-primary)", fontFamily: 'var(--font-mono)' }}>English</span>
          </div>
        </div>
      )}

      {/* Privacy Tab */}
      {activeTab === 'Privacy' && (
        <div style={{ maxWidth: 600 }}>
          {[
            { icon: '👁', label: 'Online Status', desc: 'Your online status is visible to project managers' },
            { icon: '📂', label: 'Profile Visibility', desc: 'Your profile is visible in the team directory' },
          ].map(setting => (
            <div key={setting.label} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 20px', background: "var(--bg-card)", borderRadius: 10,
              marginBottom: 8, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{setting.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{setting.label}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{setting.desc}</div>
              </div>
              <span style={{ fontSize: 12, color: "var(--text-success)", fontWeight: 500 }}>On</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 12, textAlign: 'center' }}>
            Privacy settings are managed by your organization
          </div>
        </div>
      )}
    </div>
  );
}
